import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAthleteAdditionalData,
  setLoadingAthleteAdditional,
  setErrorAthleteAdditional,
} from "../../../store/slices/additionalInfoSlice";
import { getAdditionalInfo } from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import LoadingSpinner from "../../LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import VideoPlayer from "../VideoPlayer";
import { profileVideos } from "../../../utils/profileVideo";
import { faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faPhone, faUser } from "@fortawesome/free-solid-svg-icons";
import { HoverCard } from "./hoverCard";
import InstagramPreview from "./instagramPrew";
import { useSingleActiveVideo } from "../../../hooks/useSingleActiveVideo";

export default function AthleteAdditionalInfo({ userId }) {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.additionalInfo);
  const apiInstance = useAxiosInstance();
  const [openIndex, setOpenIndex] = useState(0);
  const { pauseAll, getVideoControlProps } = useSingleActiveVideo();

  const toggleAccordion = (index) => {
    pauseAll();
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      dispatch(setLoadingAthleteAdditional(true));
      try {
        const response = await getAdditionalInfo(apiInstance, userId);
        dispatch(setAthleteAdditionalData(response));
      } catch (err) {
        dispatch(setErrorAthleteAdditional(err.message));
      } finally {
        dispatch(setLoadingAthleteAdditional(false));
      }
    };

    fetchData();
    // Removed handleResize and window.addEventListener("resize", handleResize)
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkLabels = {
    hudlFilmUrl: "HUDL Film",
    basketballHighlightsUrl: "Basketball Highlights",
    track100mVideoUrl: "Track 100m Video",
    sevenOnSevenHighlightsUrl: "7-on-7 Highlights",
    oneOnOnesVideoUrl: "One-on-Ones Video",
  };

  function titleVideo(data) {
    const video = profileVideos.find((v) => v.id === data.type);
    return video?.name || "No Title";
  }

  const accordionData = useMemo(() => {
    if (!data) return [];

    return [
      {
        title: "Offers",
        content: (
          <>
            {data.offers?.map((offer, index) => (
              <div
                key={index}
                className="w-full bg-white border border-gray-200 rounded-md p-3 mb-2"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-0">School</p>
                    <p className="text-base font-normal text-gray-900">
                      {offer.schoolName || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-0">Division</p>
                    <p className="text-base font-normal text-gray-900">
                      {offer.division ?? "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-0">Year</p>
                    <p className="text-base font-normal text-gray-900">
                      {offer.offerYear || "-"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </>
        ),
        defaultOpen: true,
      },

      {
        title: "Accolades",
        content: (
          <>
            {data.accolades?.map((accolade, index) => (
              <div
                key={index}
                className="w-full bg-white border border-gray-200 rounded-md p-3 mb-2"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-0">Description</p>
                    <p className="text-base font-normal text-gray-900">
                      {accolade.accoladeDescription}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-0">Classification</p>
                    <p className="text-base font-normal text-gray-900">
                      {accolade.accoladeClassification ?? "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-0">Year</p>
                    <p className="text-base font-normal text-gray-900">
                      {accolade.accoladeYear}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </>
        ),
      },

      {
        title: "Personal Bio",
        content: <p className="text-gray-700">{data.bio}</p>,
      },
      {
        title: "Profile Videos",
        content: (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 justify-items-center">
            {data.profileVideos?.map((video, index) => (
              <div key={index} className="flex flex-col items-start w-full">
                <VideoPlayer
                  src={video.filePath}
                  width="100%"
                  height="160px"
                  {...getVideoControlProps(`profile-video-${index}`)}
                />
                <span className="pt-2 text-left font-medium">
                  {titleVideo(video)}
                </span>
              </div>
            ))}
          </div>
        ),
      },
      {
        title: "Weight Room Videos",
        content: (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6">
            {data.weightRoomVideos
              ?.filter((video) => video.filePath)
              .map((video, index) => (
                <div key={index}>
                  <VideoPlayer
                    src={video.filePath}
                    {...getVideoControlProps(`weight-room-video-${index}`)}
                  />
                  <span className="block font-medium pt-2">
                    {video.title} - {video.weight} Pounds x {video.reps}
                  </span>
                </div>
              ))}
          </div>
        ),
      },
      {
        title: "Video Links",
        content: (
          <div className="space-y-3">
            {Object.entries(data.profileVideoLinks).map(([key, url]) =>
              url ? (
                <div key={key}>
                  <span className="font-medium">{linkLabels[key] || key}:</span>
                  <span className="block">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {url}
                    </a>
                  </span>
                </div>
              ) : null,
            )}
          </div>
        ),
      },
    ];
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading)
    return (
      <div className="mt-4 text-center text-lg font-semibold">
        <LoadingSpinner />
      </div>
    );

  if (error)
    return (
      <p className="text-center text-red-500 font-semibold">Error: {error}</p>
    );

  return (
    <>
      <div className="pt-0 px-4 -m-4 mt-0 overflow-y-auto pb-2 xl:pb-0 lg:pb-0">
        <div className="grid grid-cols-2 items-center gap-4 mb-4">
          <div className="align-self-start items-center">
            <h2 className="text-lg font-bold">Contact Info</h2>{" "}
          </div>
          <div className="mt-3 mb-0 text-right">
            <span className="border border-red-500 rounded-md p-2 text-gray-800 text-sm inline-flex">
              <img
                className="h-5 mr-2"
                alt="Not Verified"
                src="/notverified.svg"
              />{" "}
              Not verified.
            </span>
          </div>
        </div>
        <div className="mt-0 space-y-2">
          {data?.contact && (
            <div className="text-gray-800 mb-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-0 gap-y-6">
                {[
                  {
                    icon: faInstagram,
                    label: "Instagram",
                    value: data.contact.instagram,
                    link: data.contact.instagram
                      ? `https://instagram.com/${data.contact.instagram}`
                      : null,
                    type: "instagram",
                  },
                  {
                    icon: faXTwitter,
                    label: "X",
                    value: data.contact.twitter,
                    link: data.contact.twitter
                      ? `https://twitter.com/${data.contact.twitter}`
                      : null,
                  },
                  {
                    icon: faEnvelope,
                    label: "Athlete Email",
                    value: data.contact.athleteEmail,
                    link: data.contact.athleteEmail
                      ? `mailto:${data.contact.athleteEmail}`
                      : null,
                  },
                  {
                    icon: faEnvelope,
                    label: "Parent Email",
                    value: data.contact.parentEmail,
                    link: data.contact.parentEmail
                      ? `mailto:${data.contact.parentEmail}`
                      : null,
                  },
                  {
                    icon: faPhone,
                    label: "Athlete Phone",
                    value: data.contact.athletePhone,
                  },
                  {
                    icon: faPhone,
                    label: "Parent Phone",
                    value: data.contact.parentPhone,
                  },
                ].map(({ icon, label, value, link, type }) => {
                  const content = (
                    <div className="flex items-center space-x-2">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100">
                        <FontAwesomeIcon
                          icon={icon}
                          className="text-base text-gray-600"
                        />
                      </div>
                      <div className="w-[80%] truncate">
                        <p className="text-sm font-semibold text-gray-600">
                          {label}
                        </p>
                        <p className="text-sm text-gray-500">
                          {link ? (
                            <a
                              href={link}
                              className="text-blue-600 hover:underline"
                            >
                              {value || "N/A"}
                            </a>
                          ) : (
                            <span>{value || "N/A"}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                  if (type === "instagram" && value) {
                    return (
                      <HoverCard key={label} trigger={content}>
                        <InstagramPreview username={data.contact.instagram} />
                      </HoverCard>
                    );
                  }
                  return <div key={label}> {content} </div>;
                })}
              </div>
            </div>
          )}

          {data?.highSchoolContactInfo && (
            <div className="text-gray-800 mb-6">
              <h2 className="text-lg font-bold mb-4 border-b pb-2">
                Coach Contact Info
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-0 gap-y-6">
                {[
                  {
                    icon: faUser,
                    label: "Coach Name",
                    value: [
                      data.highSchoolContactInfo.firstName,
                      data.highSchoolContactInfo.lastName,
                    ]
                      .filter(Boolean)
                      .join(" "),
                  },
                  {
                    icon: faPhone,
                    label: "Coach Phone",
                    value: data.highSchoolContactInfo.phoneNumber,
                  },
                ].map(({ icon, label, value }) => (
                  <div key={label}>
                    <div className="flex items-center space-x-2">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100">
                        <FontAwesomeIcon
                          icon={icon}
                          className="text-base text-gray-600"
                        />
                      </div>
                      <div className="w-[80%] truncate">
                        <p className="text-sm font-semibold text-gray-600">
                          {label}
                        </p>
                        <p className="text-sm text-gray-500">
                          <span>{value || "N/A"}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data?.gpa && (
            <div className="text-gray-800 pb-0 flex flex-col">
              <h2 className="text-lg font-bold my-4 border-b">GPA</h2>
              <div className="flex items-center justify-around gap-4">
                <span className="text-lg font-semibold">{data.gpa}</span>
                {data.transcriptFilePath && (
                  <a
                    href={data.transcriptFilePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View Transcript
                  </a>
                )}
              </div>
            </div>
          )}

          {data?.profileVideos?.some(
            (video) => titleVideo(video) === "Hudl Film",
          ) ? (
            <div className="mb-4 pb-4">
              <h2 className="text-lg font-bold mb-4 border-b pb-2">
                Hudl Film
              </h2>
              <VideoPlayer
                src={
                  data.profileVideos.find(
                    (video) => titleVideo(video) === "Hudl Film",
                  ).filePath
                }
                width="100%"
                height="300px"
                overlay={false}
                {...getVideoControlProps("hudl-film")}
              />
            </div>
          ) : data?.profileVideoLinks?.hudlFilmUrl ? (
            <div className="mb-6 pb-4">
              <h2 className="text-2xl font-bold mb-4 border-b pb-2">
                🎥 Hudl Film Link
              </h2>
              <a
                href={data.profileVideoLinks.hudlFilmUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {data.profileVideoLinks.hudlFilmUrl}
              </a>
            </div>
          ) : null}

          {accordionData.map((item, index) => (
            <div key={index} className="border rounded shadow-sm">
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full flex justify-between items-center px-4 py-3 font-semibold bg-gray-100 hover:bg-gray-200 focus:outline-none"
              >
                <span>{item.title}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`transition-transform duration-300 ${openIndex === index ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-auto transition-all duration-300  bg-white ${
                  openIndex === index ? " p-4" : "max-h-0 p-0"
                }`}
              >
                <div className="text-sm">{item.content}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
